<template>
  <div>
    <!-- Tabs -->
    <div class="admin-toolbar">
      <div class="inline-flex rounded-md border border-gray-200 bg-white p-1 shadow-sm">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="currentTab = tab.id"
          :class="['rounded px-4 py-1.5 text-sm font-semibold transition-colors', currentTab === tab.id ? 'bg-brand-red text-white' : 'text-gray-600 hover:bg-gray-100']"
        >
          {{ tab.label }}
          <span class="ml-1 opacity-70 tabular-nums">({{ tab.id === 'pending' ? pendingCount : ads.length }})</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="admin-empty">Loading advertisements...</div>

    <div v-else-if="error" class="admin-empty text-red-600">{{ error }}</div>

    <!-- Empty State -->
    <div v-else-if="filteredAds.length === 0" class="admin-empty">
      <i class="fa-solid fa-bullhorn text-2xl text-gray-300"></i>
      {{ currentTab === 'pending' ? 'No advertisements waiting for approval.' : 'No advertisements found.' }}
    </div>

    <!-- Ad List -->
    <div v-else class="grid grid-cols-1 gap-4">
      <div
        v-for="ad in filteredAds"
        :key="ad.id"
        class="card flex flex-col gap-4 p-4 md:flex-row"
      >
        <!-- Image -->
        <div class="h-40 w-full flex-shrink-0 overflow-hidden rounded-md bg-gray-100 md:h-28 md:w-40">
          <img
            v-if="ad.image"
            :src="resolveImage(ad.image)"
            :alt="ad.title"
            class="h-full w-full object-cover"
          />
          <div v-else class="flex h-full w-full items-center justify-center text-xs text-gray-400">No Image</div>
        </div>

        <!-- Content -->
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-3">
            <h3 class="font-serif text-lg font-bold text-gray-900">{{ ad.title }}</h3>
            <span :class="['badge', ad.is_approved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700']">
              {{ ad.is_approved ? 'Approved' : 'Pending' }}
            </span>
          </div>
          <p class="mt-1 whitespace-pre-wrap text-sm text-gray-600">{{ ad.description }}</p>
          <p class="mt-2 text-xs text-gray-400">
            Publish date: {{ formatDate(ad.publish_date) }} · Submitted by user #{{ ad.user_id }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2 border-t border-gray-100 pt-3 md:flex-col md:justify-center md:border-l md:border-t-0 md:pl-4 md:pt-0">
          <button v-if="!ad.is_approved" @click="approveAd(ad.id)" class="btn-success btn-sm">Approve</button>
          <button @click="deleteAd(ad.id)" class="btn-danger btn-sm">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import api, { errorMessage, FILE_BASE_URL } from '@/api';

export default {
  name: 'AdminAds',
  data() {
    return {
      currentTab: 'pending',
      tabs: [
        { id: 'pending', label: 'Pending Approval' },
        { id: 'all', label: 'All Advertisements' },
      ],
      ads: [],
      loading: true,
      error: '',
    };
  },
  computed: {
    filteredAds() {
      if (this.currentTab === 'pending') {
        return this.ads.filter(ad => !ad.is_approved);
      }
      return this.ads;
    },
    pendingCount() {
      return this.ads.filter(ad => !ad.is_approved).length;
    },
  },
  mounted() {
    this.fetchAds();
  },
  methods: {
    async fetchAds() {
      this.loading = true;
      this.error = '';
      try {
        const res = await api.get('/admin/advertisments');
        this.ads = res.data;
      } catch (err) {
        console.error("Error fetching ads:", err);
        this.error = errorMessage(err, 'Failed to load advertisements');
      } finally {
        this.loading = false;
      }
    },
    async approveAd(id) {
      const result = await Swal.fire({
        title: 'Approve Advertisement?',
        text: "It will appear in the Advertisements sidebar.",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#059669',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, approve!'
      });

      if (!result.isConfirmed) return;

      try {
        await api.put(`/admin/advertisments/${id}/approve`);
        const ad = this.ads.find(a => a.id === id);
        if (ad) ad.is_approved = 1;
        Swal.fire({ icon: 'success', title: 'Approved!', text: 'Advertisement approved.', timer: 1600, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to approve.'), confirmButtonColor: '#BF3131' });
      }
    },
    async deleteAd(id) {
      const result = await Swal.fire({
        title: 'Delete Advertisement?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DC2626',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) return;

      try {
        await api.delete(`/admin/advertisments/${id}`);
        this.ads = this.ads.filter(a => a.id !== id);
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Advertisement deleted.', timer: 1600, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete.'), confirmButtonColor: '#BF3131' });
      }
    },
    resolveImage(img) {
      if (!img) return '';
      if (img.startsWith('data:') || img.startsWith('http')) return img;
      return `${FILE_BASE_URL}${img}`;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
};
</script>
